import type { SavedView, ViewFilters } from "../../domain/app-types.ts";
import type { Todo } from "../../domain/entities.ts";
import { defaultSavedView, defaultSavedViewFilters, matchesSavedViewFilters, savedViewsForRoot, type SavedViewFilterContext } from "./saved-view-state.ts";

export interface SavedViewRuntimeCapabilities {
  views(): SavedView[];
  updatePersonalViews(): SavedView[];
  activateState(view: SavedView): number;
  setPageSize(value: number): void;
  resetPage(): void;
  clearCategory(): void;
  persistPersonalViews(views: SavedView[]): Promise<void>;
  changePublished(id: string, rootId: string, update: (view: SavedView) => void): Promise<void>;
  markLocal(): void;
  sync(): void;
  filterContext: SavedViewFilterContext;
}

export function createSavedViewRuntimeController(capabilities: SavedViewRuntimeCapabilities) {
  async function persistPersonal(): Promise<void> {
    await capabilities.persistPersonalViews(capabilities.updatePersonalViews());
  }

  async function update(id: string, change: (view: SavedView) => void): Promise<void> {
    const view = capabilities.views().find(item => item.id === id);
    if (!view) return;
    if (view.published && view.rootId) {
      await capabilities.changePublished(id, view.rootId, change);
      capabilities.markLocal();
      capabilities.sync();
    }
    change(view);
    await persistPersonal();
  }

  function activate(view: SavedView): void {
    capabilities.setPageSize(capabilities.activateState(view));
    capabilities.resetPage();
    capabilities.clearCategory();
  }

  return {
    defaultFilters: (): ViewFilters => defaultSavedViewFilters(),
    viewsForRoot: (rootId: string | null): SavedView[] => savedViewsForRoot(capabilities.views(), rootId),
    defaultForRoot: (rootId: string): SavedView | undefined => defaultSavedView(savedViewsForRoot(capabilities.views(), rootId)),
    persistPersonal,
    update,
    activate,
    matches: (todo: Todo, filters: ViewFilters): boolean => matchesSavedViewFilters(todo, filters, capabilities.filterContext),
  };
}
